(() => {
  const RANGES = {
    sph: { min: -20, max: 12, step: 0.25, zero: '0.00 (Plano)' },
    cyl: { min: -10, max: 6, step: 0.25, zero: '0.00 (None)' },
    add: { min: 0.75, max: 4, step: 0.25, blank: 'None' },
  };

  const formatDiopter = (value) => (value > 0 ? '+' : value < 0 ? '−' : '') + Math.abs(value).toFixed(2);

  class CartierRxQuiz {
    constructor(root) {
      this.root = root;
      this.steps = Array.from(root.querySelectorAll('[data-rx-step]'));
      this.done = root.querySelector('[data-rx-done]');
      this.nextButton = root.querySelector('[data-rx-next]');
      this.backButton = root.querySelector('[data-rx-back]');
      this.nav = root.querySelector('[data-rx-nav]');
      this.error = root.querySelector('[data-rx-error]');
      this.progress = root.querySelector('[data-rx-progress]');
      this.progressText = root.querySelector('[data-rx-progress-text]');
      this.progressBar = root.querySelector('[data-rx-progress-bar]');
      this.frames = root.querySelector('[data-rx-frames]');
      this.current = 'intent';
      this.frameCount = 0;
      this.lastIntentAt = 0;
      this.advanceTimer = null;
      this.transitioning = false;
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.partialSent = false;
      this.sendPartial = root.dataset.sendPartial === 'true';

      this.countCurrent = root.querySelector('[data-rx-count-current]');
      this.countTotal = root.querySelector('[data-rx-count-total]');

      this.buildRxSelects();
      this.decorate();
      this.bind();
      this.applyConditions();
      this.render(false);
      this.applyStartParam();
      this.watchForChat();
    }

    chatOpener() {
      const inbox = document.querySelector('shopify-chat');
      if (inbox && typeof inbox.open === 'function') {
        return () => {
          try {
            inbox.open();
          } catch (error) {
            if (typeof inbox.show === 'function') inbox.show();
          }
        };
      }
      const activator = inbox && inbox.shadowRoot && inbox.shadowRoot.querySelector('button.activator');
      if (activator) return () => activator.click();
      const w = window;
      if (w.GorgiasChat && typeof w.GorgiasChat.open === 'function') return () => w.GorgiasChat.open();
      if (w.tidioChatApi && typeof w.tidioChatApi.open === 'function') return () => w.tidioChatApi.open();
      if (w.$crisp && typeof w.$crisp.push === 'function') return () => w.$crisp.push(['do', 'chat:open']);
      if (w.Tawk_API && typeof w.Tawk_API.maximize === 'function') return () => w.Tawk_API.maximize();
      if (typeof w.Intercom === 'function') return () => w.Intercom('show');
      if (typeof w.zE === 'function') return () => w.zE('messenger', 'open');
      return null;
    }

    watchForChat() {
      const button = this.root.querySelector('[data-rx-chat]');
      const line = this.root.querySelector('[data-rx-chat-line]');
      if (!button) return;
      button.addEventListener('click', () => this.openChat());
      let tries = 0;
      const look = () => {
        if (this.chatOpener()) {
          button.hidden = false;
          if (line && line.textContent.trim()) line.hidden = false;
          return;
        }
        tries += 1;
        if (tries < 40) setTimeout(look, 500);
      };
      look();
    }

    applyStartParam() {
      let start = '';
      try {
        start = (new URLSearchParams(window.location.search).get('start') || '').toLowerCase();
      } catch (error) {
        return;
      }
      if (!start) return;
      const input = this.root.querySelector(`[name="intent"][data-start="${CSS.escape(start)}"]`);
      if (!input) return;
      input.checked = true;
      this.applyConditions();
      this.current = 'style';

      let style = '';
      try {
        style = (new URLSearchParams(window.location.search).get('style') || '').toLowerCase();
      } catch (error) {
        style = '';
      }
      const slug = (text) =>
        text
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '');
      const styleInput = style && Array.from(this.root.querySelectorAll('[name="style"]')).find((item) => slug(item.value) === style);
      if (styleInput) {
        styleInput.checked = true;
        this.showFrames(styleInput.dataset.styleId);
        this.applyConditions();
        this.current = this.sequence().includes('frame') ? 'frame' : 'correction';
      }
      this.render(false);
    }

    buildRxSelects() {
      this.root.querySelectorAll('[data-rx-range]').forEach((select) => {
        const range = RANGES[select.dataset.rxRange];
        const options = [];
        if (range.blank) options.push(new Option(range.blank, ''));
        else options.push(new Option('Select', ''));
        for (let value = range.max; value >= range.min - 0.001; value -= range.step) {
          const rounded = Math.round(value * 100) / 100;
          const label = rounded === 0 && range.zero ? range.zero : formatDiopter(rounded);
          options.push(new Option(label, formatDiopter(rounded)));
        }
        select.replaceChildren(...options);
      });
    }

    decorate() {
      this.steps.forEach((step) => {
        const legend = step.querySelector('legend.rx-quiz__question');
        if (legend && !legend.querySelector('[data-rx-qnum]')) {
          const number = document.createElement('span');
          number.className = 'rx-quiz__qnum';
          number.setAttribute('aria-hidden', 'true');
          number.dataset.rxQnum = '';
          legend.prepend(number);
        }
        if (step.querySelector('input[type="text"], input[type="email"], input[type="tel"], select, textarea')) return;
        step.dataset.rxKeys = 'true';
        step.querySelectorAll('.rx-quiz__options').forEach((group) => {
          group.querySelectorAll('.rx-quiz__option').forEach((option, index) => {
            if (index > 25 || option.querySelector('.rx-quiz__key')) return;
            const key = document.createElement('span');
            key.className = 'rx-quiz__key';
            key.setAttribute('aria-hidden', 'true');
            key.textContent = String.fromCharCode(65 + index);
            option.dataset.rxKey = key.textContent;
            option.prepend(key);
          });
        });
      });
    }

    handleShortcut(event) {
      if (event.metaKey || event.ctrlKey || event.altKey || event.key.length !== 1) return;
      if (event.target instanceof Element && event.target.closest('input[type="text"], input[type="email"], input[type="tel"], input[type="number"], input[type="date"], select, textarea, [contenteditable]')) return;
      const step = this.stepElement(this.current);
      if (!step || step.hidden || step.dataset.rxKeys !== 'true') return;
      const option = step.querySelector(`.rx-quiz__option[data-rx-key="${event.key.toUpperCase()}"]`);
      if (!option || !this.isShown(option, step)) return;
      const input = option.querySelector('input');
      event.preventDefault();
      this.lastIntentAt = Date.now();
      if (input.type === 'checkbox') input.checked = !input.checked;
      else input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      option.classList.remove('is-pressed');
      void option.offsetWidth;
      option.classList.add('is-pressed');
    }

    bind() {
      this.nextButton.addEventListener('click', () => this.next());
      this.backButton.addEventListener('click', () => this.back());
      this.root.addEventListener('change', (event) => {
        this.clearError();
        if (event.target.name === 'style') this.showFrames(event.target.dataset.styleId);
        if (event.target.matches('[data-rx-pd-help]')) this.togglePdRequired();
        if (event.target.matches('[data-rx-file]')) this.showFileName(event.target);
        if (event.target.matches('[data-rx-chat-open]') && event.target.checked) {
          this.toggleUploadRequired();
          this.openChat();
        }
        if (event.target.name === 'rx_mode') this.toggleUploadRequired();
        if (event.target.checked && event.target.dataset.rxExcludes) {
          event.target.dataset.rxExcludes.split(' ').forEach((otherId) => {
            const other = this.root.querySelector(`#${CSS.escape(otherId)}`);
            if (other) other.checked = false;
          });
        }
        this.applyConditions();
        this.updateNav();
        this.autoAdvance(event.target);
      });
      this.root.addEventListener('pointerdown', () => {
        this.lastIntentAt = Date.now();
      });
      document.addEventListener('keydown', (event) => {
        if (!this.root.isConnected) return;
        const rect = this.root.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > window.innerHeight) return;
        this.handleShortcut(event);
      });
      this.root.addEventListener('keydown', (event) => {
        if (event.key === ' ' || event.key === 'Enter') this.lastIntentAt = Date.now();
        if (event.key !== 'Enter' || event.target.tagName === 'TEXTAREA' || event.target.tagName === 'BUTTON') return;
        if (event.target.closest('[data-rx-step]')) {
          event.preventDefault();
          this.next();
        }
      });
    }

    value(name) {
      const inputs = Array.from(this.root.querySelectorAll(`[name="${name}"]`));
      const checked = inputs.filter((input) => (input.type === 'radio' || input.type === 'checkbox' ? input.checked : input.value));
      return checked.map((input) => input.value).join('|');
    }

    matches(rule) {
      const negate = rule.includes('!=');
      const [name, expected] = rule.split(negate ? '!=' : '=');
      const actual = this.value(name.trim());
      const hit = expected.split('|').some((option) => option.trim() === actual);
      return negate ? !hit : hit;
    }

    applyConditions() {
      this.root.querySelectorAll('[data-show-if]').forEach((element) => {
        element.hidden = !this.matches(element.dataset.showIf);
      });
    }

    showFrames(styleId) {
      if (!this.frames) return;
      let visible = 0;
      this.frames.querySelectorAll('[data-styles]').forEach((frame) => {
        const show = frame.dataset.styles.split(' ').includes(styleId);
        frame.hidden = !show;
        if (!show) frame.querySelector('input').checked = false;
        if (show) visible += 1;
      });
      this.frameCount = visible;
      const styleInput = this.root.querySelector(`[name="style"][data-style-id="${styleId}"]`);
      const leadId = styleInput && styleInput.dataset.leadProduct;
      if (leadId) {
        const lead = this.frames.querySelector(`[data-product-id="${leadId}"]:not([hidden])`);
        if (lead) this.frames.prepend(lead);
      }
      this.updateFrameCopy();
    }

    updateFrameCopy() {
      const title = this.root.querySelector('[data-rx-frame-title]');
      const hint = this.root.querySelector('[data-rx-frame-hint]');
      if (!title || !this.frames) return;
      const hasFamilies = Boolean(this.frames.querySelector('.rx-quiz__frame--family:not([hidden])'));
      const ownsOne = this.value('intent') === 'Prescription lenses for a Cartier I own';
      const style = this.value('style');
      if (hasFamilies) {
        title.textContent = ownsOne ? `Which ${style} design do you own?` : `Which ${style} design?`;
        if (hint) hint.textContent = "Choose the design you're drawn to. We'll confirm the exact model with you.";
      } else {
        title.textContent = ownsOne ? 'Is yours one of these?' : 'Any frame in particular?';
        if (hint) hint.textContent = "Tap a frame, or continue if you'd like us to suggest options.";
      }
    }

    autoAdvance(target) {
      if (target.type !== 'radio') return;
      const step = target.closest('[data-rx-step]');
      if (!step || step.dataset.rxStep !== this.current || !step.dataset.rxAuto) return;
      if (target.hasAttribute('data-rx-hold')) return;
      if (Date.now() - this.lastIntentAt > 1500) return;
      clearTimeout(this.advanceTimer);
      this.advanceTimer = setTimeout(() => this.next(), this.reduceMotion ? 120 : 340);
    }

    showFileName(input) {
      const label = this.root.querySelector('[data-rx-file-name]');
      if (label) label.textContent = input.files && input.files[0] ? input.files[0].name : 'Choose a photo or PDF';
    }

    toggleUploadRequired() {
      const file = this.root.querySelector('[data-rx-file]');
      if (!file) return;
      const optional = this.value('rx_mode') === 'Talk to an optician now';
      file.required = !optional;
      file.closest('.rx-quiz__dropzone').classList.toggle('is-optional', optional);
    }

    openChat() {
      const open = this.chatOpener();
      if (open) open();
    }

    togglePdRequired() {
      const pd = this.root.querySelector('[data-rx-pd]');
      const help = this.root.querySelector('[data-rx-pd-help]');
      if (pd && help) pd.required = !help.checked;
    }

    sequence() {
      const order = ['intent', 'style'];
      const style = this.value('style');
      if (this.frames && style !== 'Help me choose' && (!style || this.frameCount > 0)) order.push('frame');
      order.push('correction', 'lenses', 'contact', 'rx-status');
      if (this.value('rx_status') === 'yes') order.push('rx-entry');
      order.push('details');
      return order;
    }

    stepElement(name) {
      return this.steps.find((step) => step.dataset.rxStep === name);
    }

    isShown(element, step) {
      let node = element;
      while (node && node !== step) {
        if (node.hidden) return false;
        node = node.parentElement;
      }
      return true;
    }

    validate(step) {
      const fields = Array.from(step.querySelectorAll('input, select, textarea')).filter((field) => this.isShown(field, step));
      const seenGroups = new Set();
      for (const field of fields) {
        if (field.type === 'radio') {
          if (seenGroups.has(field.name)) continue;
          seenGroups.add(field.name);
          const group = fields.filter((item) => item.name === field.name);
          if (group.some((item) => item.required) && !group.some((item) => item.checked)) {
            return this.fail(field, 'Choose an option to continue.');
          }
          continue;
        }
        if (field.type === 'file') {
          const file = field.files && field.files[0];
          if (field.required && !file) return this.fail(field, 'Choose a photo or PDF of your prescription to continue.');
          if (file && file.size > 25 * 1024 * 1024) return this.fail(field, 'That file is over 25 MB. Choose a smaller photo or PDF.');
          continue;
        }
        if (field.type === 'checkbox' && field.required && !field.checked) {
          return this.fail(field, 'Check the consent box to continue.');
        }
        if (field.required && !field.value.trim()) {
          return this.fail(field, `${field.tagName === 'SELECT' ? 'Choose' : 'Enter'} ${this.describe(field)} to continue.`);
        }
        if (field.value && !field.checkValidity()) {
          return this.fail(field, `Check ${this.describe(field)}. It doesn't look right.`);
        }
      }

      return true;
    }

    validateRx(step) {
      for (const eye of ['od', 'os']) {
        const cyl = step.querySelector(`[name="${eye}_cyl"]`);
        const axis = step.querySelector(`[name="${eye}_axis"]`);
        const hasCyl = cyl.value && !cyl.value.startsWith('0');
        if (hasCyl && !axis.value) return this.fail(axis, 'Enter the axis for any eye with a CYL value.');
      }
      const pd = step.querySelector('[data-rx-pd]');
      if (pd.value && !/^\s*\d{2}(\.\d)?\s*(\/\s*\d{2}(\.\d)?\s*)?$/.test(pd.value)) {
        return this.fail(pd, 'Enter PD as one number, like 63, or two, like 31.5/31.5.');
      }
      return true;
    }

    describe(field) {
      const label = field.labels && field.labels[0] ? field.labels[0].textContent : field.getAttribute('aria-label') || 'this field';
      return label.replace(/Optional/i, '').replace(/,.*$/, '').trim().toLowerCase();
    }

    fail(field, message) {
      this.error.textContent = message;
      this.error.hidden = false;
      field.setAttribute('aria-invalid', 'true');
      field.focus({ preventScroll: false });
      return false;
    }

    clearError() {
      this.error.hidden = true;
      this.error.textContent = '';
      this.root.querySelectorAll('[aria-invalid]').forEach((field) => field.removeAttribute('aria-invalid'));
    }

    async next() {
      if (this.transitioning) return;
      clearTimeout(this.advanceTimer);
      const step = this.stepElement(this.current);
      this.clearError();
      if (!this.validate(step)) return;

      const order = this.sequence();
      const index = order.indexOf(this.current);

      if (this.current === 'contact' && this.sendPartial && !this.partialSent) {
        this.partialSent = true;
        this.submit('Contact details only (quiz not finished)', ['rx-status', 'rx-entry', 'details']).catch(() => {});
      }

      if (index === order.length - 1) {
        await this.finish();
        return;
      }
      this.current = order[index + 1];
      this.render(true, 'forward');
    }

    back() {
      if (this.transitioning) return;
      clearTimeout(this.advanceTimer);
      const order = this.sequence();
      const index = order.indexOf(this.current);
      if (index <= 0) return;
      this.clearError();
      this.current = order[index - 1];
      this.render(true, 'back');
    }

    transition(incoming, direction, animate, onShown) {
      const outgoing = [...this.steps, this.done].find((element) => element && !element.hidden && element !== incoming);
      const show = () => {
        [...this.steps, this.done].forEach((element) => {
          if (!element) return;
          element.classList.remove('is-leaving-forward', 'is-leaving-back', 'is-entering-forward', 'is-entering-back');
          element.hidden = element !== incoming;
        });
        if (animate && !this.reduceMotion) {
          void incoming.offsetWidth;
          incoming.classList.add(`is-entering-${direction}`);
          incoming.addEventListener('animationend', () => incoming.classList.remove(`is-entering-${direction}`), { once: true });
        }
        if (onShown) onShown();
        this.transitioning = false;
      };
      if (outgoing && animate && !this.reduceMotion) {
        this.transitioning = true;
        outgoing.classList.add(`is-leaving-${direction}`);
        setTimeout(show, 200);
      } else {
        show();
      }
    }

    updateNav() {
      const order = this.sequence();
      const index = order.indexOf(this.current);
      const step = this.stepElement(this.current);
      const needsChoice = step && step.dataset.rxAuto === 'required' && !step.querySelector('input[type="radio"]:checked');
      this.backButton.hidden = index === 0;
      this.nextButton.hidden = Boolean(needsChoice);
      this.nextButton.textContent = index === order.length - 1 ? 'Send my request' : 'Continue';
      this.nav.classList.toggle('rx-quiz__nav--empty', this.backButton.hidden && this.nextButton.hidden);
    }

    render(moveFocus, direction = 'forward') {
      const order = this.sequence();
      const index = order.indexOf(this.current);
      const incoming = this.stepElement(this.current);
      if (this.current === 'frame') this.updateFrameCopy();
      const percent = Math.round(((index + 1) / order.length) * 100);
      this.progressText.textContent = `Step ${index + 1} of ${order.length}`;
      const pad = (value) => String(value).padStart(2, '0');
      if (this.countCurrent) this.countCurrent.textContent = pad(index + 1);
      if (this.countTotal) this.countTotal.textContent = pad(order.length);
      const qnum = incoming.querySelector('[data-rx-qnum]');
      if (qnum) qnum.textContent = String(index + 1);
      this.progressBar.setAttribute('aria-valuenow', String(percent));
      this.progressBar.style.setProperty('--rx-progress', `${percent}%`);
      this.transition(incoming, direction, moveFocus, () => {
        this.updateNav();
        if (moveFocus) this.focusStep(incoming);
      });
    }

    focusStep(container) {
      const heading = container.querySelector('.rx-quiz__question');
      const top = this.root.querySelector('.rx-quiz__card').getBoundingClientRect().top;
      if (top < 0 || top > window.innerHeight * 0.3) {
        window.scrollTo({ top: window.scrollY + top - 24, behavior: this.reduceMotion ? 'auto' : 'smooth' });
      }
      if (heading) heading.focus({ preventScroll: true });
    }

    collect(excludeSteps = []) {
      const lines = [];
      const seen = new Set();
      this.sequence()
        .filter((name) => !excludeSteps.includes(name))
        .forEach((name) => {
          const step = this.stepElement(name);
          step.querySelectorAll('[data-field]').forEach((field) => {
            if (!this.isShown(field, step)) return;
            const label = field.dataset.field;
            if (seen.has(label)) return;
            const group = Array.from(step.querySelectorAll(`[data-field="${label}"]`)).filter((item) => this.isShown(item, step));
            const values = group
              .filter((item) => (item.type === 'radio' || item.type === 'checkbox' ? item.checked : item.value.trim()))
              .map((item) => item.dataset.label || item.value.trim());
            seen.add(label);
            if (values.length) lines.push([label, values.join(', ')]);
          });
        });
      return lines;
    }

    selectedFile() {
      const input = this.root.querySelector('[data-rx-file]');
      const step = this.stepElement('rx-entry');
      if (!input || !input.files || !input.files[0]) return null;
      if (!this.sequence().includes('rx-entry') || !this.isShown(input, step)) return null;
      return input.files[0];
    }

    async submit(stage, excludeSteps, options = {}) {
      const endpoint = this.root.dataset.endpoint;
      const sent = endpoint
        ? await this.submitFormspree(endpoint, stage, excludeSteps, options)
        : await this.submitShopify(stage, excludeSteps);
      try {
        await this.sendToKlaviyo(stage);
      } catch (error) {
        /* Klaviyo is optional; never block the lead */
      }
      return sent;
    }

    async sendToKlaviyo(stage) {
      const klaviyo = window.klaviyo;
      const email = this.value('email');
      if (!klaviyo || !email) return;
      const shareable = [
        'Looking for',
        'Style',
        'Frame',
        'Style number',
        'Cartier logo engraved',
        'Needed by',
        'Budget, frame and lenses',
        'Payment',
        'Preferred contact',
        'Shipping state',
      ];
      const flatKeys = {
        'Looking for': 'quiz_looking_for',
        Style: 'quiz_style',
        Frame: 'quiz_frame',
        'Style number': 'quiz_style_number',
        'Cartier logo engraved': 'quiz_engraving',
        'Needed by': 'quiz_timing',
        'Budget, frame and lenses': 'quiz_budget',
        Payment: 'quiz_payment',
        'Preferred contact': 'quiz_contact_preference',
        'Shipping state': 'quiz_shipping_state',
      };
      const estimate = this.quote();
      const properties = { 'Lead stage': stage, 'Lead source': 'Cartier Rx quiz', 'Quiz page': window.location.pathname };
      if (estimate) {
        properties['Estimate shown'] = estimate.text;
        properties.quiz_estimate = estimate.text;
      }
      this.collect([]).forEach(([label, value]) => {
        if (!shareable.includes(label)) return;
        properties[label] = value;
        if (flatKeys[label]) properties[flatKeys[label]] = value;
      });
      const wantsEmail = this.root.querySelector('[name="email_copy"]');
      const profile = {
        $email: email,
        $first_name: this.value('first_name') || undefined,
        $last_name: this.value('last_name') || undefined,
        'Cartier Rx quiz lead': true,
        'Cartier Rx quiz opt-in': Boolean(wantsEmail && wantsEmail.checked),
      };
      if (this.value('ship_state')) profile['Shipping state'] = this.value('ship_state');
      klaviyo.identify(profile);
      const metric = this.root.dataset.klaviyoMetric || 'Cartier Rx Quiz Submitted';
      klaviyo.track(this.isTest() ? `${metric} (TEST)` : metric, properties);

      const listId = this.isTest() ? '' : this.root.dataset.klaviyoList;
      const companyId = this.root.dataset.klaviyoCompany;
      if (!listId || !companyId || !wantsEmail || !wantsEmail.checked || this.subscribed) return;
      this.subscribed = true;
      await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(companyId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2026-07-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Cartier Rx quiz',
              profile: {
                data: {
                  type: 'profile',
                  attributes: { email, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } },
                },
              },
            },
            relationships: { list: { data: { type: 'list', id: listId } } },
          },
        }),
      });
    }

    async submitFormspree(endpoint, stage, excludeSteps, { includeFile = true } = {}) {
      const body = new FormData();
      const name = [this.value('first_name'), this.value('last_name')].filter(Boolean).join(' ');
      const estimate = this.quote();
      const prefix = this.isTest() ? '[TEST] ' : '';
      body.append('_subject', `${prefix}Cartier Rx quiz: ${name || 'New lead'} (${stage})`);
      if (estimate) body.append('Estimate shown', estimate.text);
      if (this.isTest()) body.append('Test submission', 'Yes - ignore, from the test page');
      body.append('email', this.value('email'));
      body.append('Lead source', 'Cartier Rx quiz');
      body.append('Lead stage', stage);
      const honeypot = this.root.querySelector('[data-rx-honeypot]');
      if (honeypot && honeypot.value) body.append('_gotcha', honeypot.value);
      this.collect(excludeSteps).forEach(([label, value]) => {
        if (label === 'Email') return;
        body.append(label, value);
      });
      const family = this.root.querySelector('.rx-quiz__frame--family input:checked');
      if (family && family.dataset.models) body.append('Possible models', family.dataset.models);
      const file = excludeSteps.includes('rx-entry') ? null : this.selectedFile();
      if (file && includeFile) body.append('upload', file, file.name);
      if (file && !includeFile) body.append('Prescription photo', 'Upload failed. Follow up to collect it.');
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body,
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        const message = Array.isArray(data.errors) ? data.errors.map((item) => item.message).join(' ') : data.error;
        const error = new Error(message || 'Submission failed');
        error.hadFile = Boolean(file && includeFile);
        throw error;
      }
      return response;
    }

    price(key, fallback) {
      const value = parseFloat(this.root.dataset[key]);
      return Number.isFinite(value) ? value : fallback;
    }

    quote() {
      if (this.root.dataset.quote !== 'true') return null;
      const money = (value) => `$${Math.round(value).toLocaleString('en-US')}`;
      const lines = [];
      let min = 0;
      let max = 0;

      const frame = this.root.querySelector('[name="frame"]:checked');
      const style = this.root.querySelector('[name="style"]:checked');
      const framePrice = frame && parseFloat(frame.closest('.rx-quiz__frame').dataset.price || frame.dataset.price);
      if (Number.isFinite(framePrice) && framePrice > 0) {
        min += framePrice;
        max += framePrice;
        const label = frame.closest('.rx-quiz__frame').querySelector('.rx-quiz__frame-name').textContent.trim();
        lines.push([label, money(framePrice)]);
      } else if (style) {
        const lo = parseFloat(style.dataset.priceMin);
        const hi = parseFloat(style.dataset.priceMax);
        if (Number.isFinite(lo) && lo > 0) {
          min += lo;
          max += Number.isFinite(hi) && hi > lo ? hi : lo;
          lines.push([`${style.value} frame`, hi > lo ? `${money(lo)} – ${money(hi)}` : money(lo)]);
        }
      }

      const correction = this.value('correction');
      const sv = this.price('priceSv', 595);
      const progressive = this.price('priceProgressive', 695);
      if (correction === 'Progressive') {
        min += progressive;
        max += progressive;
        lines.push(['Progressive lenses', money(progressive)]);
      } else if (correction === 'Single vision') {
        min += sv;
        max += sv;
        lines.push(['Single vision lenses', money(sv)]);
      } else {
        min += sv;
        max += progressive;
        lines.push(['Lenses', `${money(sv)} – ${money(progressive)}`]);
      }

      const chosen = this.value('lenses').split('|').filter(Boolean);
      const picked = (name) => chosen.includes(name);
      const add = (label, amount) => {
        min += amount;
        max += amount;
        lines.push([label, `+${money(amount)}`]);
      };
      if (picked('Anti-glare')) add('Anti-glare', this.price('priceAr', 95));
      if (picked('Transition')) add('Transition', this.price('priceTransition', 200));
      if (picked('Tint or gradient')) add('Tint or gradient', this.price('priceTint', 65));
      const edge = picked('Edge treatment');
      if (edge) add('Edge treatment, logo included', this.price('priceEdge', 300));
      if (!edge && this.value('engraving') === 'Yes') add('Cartier logo engraved', this.price('priceLogo', 350));
      if (picked('Optician to recommend')) max += this.price('priceAr', 95);

      if (!lines.length || max <= 0) return null;
      return { min, max, lines, text: min === max ? money(min) : `${money(min)} – ${money(max)}` };
    }

    renderQuote() {
      const box = this.root.querySelector('[data-rx-quote]');
      if (!box) return;
      const quote = this.quote();
      if (!quote) {
        box.hidden = true;
        return;
      }
      this.root.querySelector('[data-rx-quote-range]').textContent = quote.text;
      const list = this.root.querySelector('[data-rx-quote-lines]');
      list.replaceChildren(
        ...quote.lines.map(([label, amount]) => {
          const li = document.createElement('li');
          const name = document.createElement('span');
          name.textContent = label;
          const value = document.createElement('span');
          value.textContent = amount;
          li.append(name, value);
          return li;
        })
      );
      box.hidden = false;
    }

    isTest() {
      return this.root.dataset.testMode === 'true';
    }

    trackConversion() {
      if (this.isTest() || this.converted) return;
      this.converted = true;
      const leadId = `rxq-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
      const sendTo = this.root.dataset.adsSendTo;
      try {
        if (typeof window.gtag === 'function') {
          if (sendTo) {
            window.gtag('config', sendTo.split('/')[0], { send_page_view: false });
            window.gtag('event', 'conversion', { send_to: sendTo, transaction_id: leadId });
          }
          window.gtag('event', 'generate_lead', { lead_source: 'cartier_rx_quiz' });
        }
      } catch (error) {
        /* tracking must never block the lead */
      }
      try {
        if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
          window.Shopify.analytics.publish('cartier_rx_quiz_lead', { lead_id: leadId, lead_source: 'cartier_rx_quiz' });
        }
      } catch (error) {
        /* tracking must never block the lead */
      }
    }

    async submitShopify(stage, excludeSteps) {
      const body = new URLSearchParams();
      body.append('form_type', 'contact');
      body.append('utf8', '✓');
      body.append('contact[email]', this.value('email'));
      body.append('contact[Lead source]', 'Cartier Rx quiz');
      body.append('contact[Lead stage]', stage);
      this.collect(excludeSteps).forEach(([label, value]) => {
        if (label === 'Email') return;
        body.append(`contact[${label}]`, value);
      });
      const response = await fetch(window.Shopify && window.Shopify.routes ? `${window.Shopify.routes.root}contact` : '/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
        credentials: 'same-origin',
      });
      if (!response.ok || /\/challenge/.test(response.url)) throw new Error('Submission blocked');
      return response;
    }

    async finish() {
      this.nextButton.disabled = true;
      this.nextButton.setAttribute('aria-busy', 'true');
      this.nextButton.textContent = 'Sending…';
      try {
        try {
          await this.submit('Completed quiz', []);
        } catch (error) {
          if (!error.hadFile) throw error;
          await this.submit('Completed quiz', [], { includeFile: false });
          this.uploadFailed = true;
        }
        const failedNote = this.root.querySelector('[data-rx-upload-failed]');
        this.progress.hidden = true;
        this.nav.hidden = true;
        this.renderQuote();
        this.trackConversion();
        this.transition(this.done, 'forward', true, () => {
          this.applyConditions();
          if (failedNote) failedNote.hidden = !this.uploadFailed;
          this.focusStep(this.done);
        });
      } catch (error) {
        this.nextButton.disabled = false;
        this.nextButton.removeAttribute('aria-busy');
        this.nextButton.textContent = 'Send my request';
        this.error.textContent = "Your request didn't go through. Try again, or call us at 833-EYE-GEMS and we'll take it by phone.";
        this.error.hidden = false;
      }
    }
  }

  const init = () => {
    document.querySelectorAll('[data-rx-quiz]').forEach((root) => {
      if (!root.cartierRxQuiz) root.cartierRxQuiz = new CartierRxQuiz(root);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
